import SharedStorageInterface from "./SharedStorageInterface.js"

class SharedStoragePersistDecorator extends SharedStorageInterface {
    constructor(storage, storageInitValue) {
        super();
        this.storage = storage;
        this.storageInitValue = storageInitValue;
        this.persistKey = "shared-storage-persist";
        this.isStorageRehydrated = false;
    }

    get = async (key=null) => {
        await this._rehydrateStorage();
        return await this.storage.get(key);
    }

    set = async (key, value) => {
        this._setPersistStorageKey(key, value);
        await this.storage.set(key, value);
        return this;
    }

    reset = async (key=null) => {
        await this.storage.reset(key);
        
        // if a key is specified, then only reset that key
        if (key) {
            this._setPersistStorageKey(key, this.storageInitValue[key])
            return this;
        }

        // otherwise, reset all keys
        this._setPersistStorage(this.storageInitValue);
        return this;
    }

    subscribe = async (key, onChange) => {
        await this._rehydrateStorage();
        return this.storage.subscribe(key, onChange);
    }

    // returns the persisted storage object
    _getPersistStorage = () => {
        const storageJson = window.sessionStorage.getItem(this.persistKey);
        if (!storageJson) return {};
        const parsedValue = JSON.parse(storageJson)
        const deserializedValue = this._deserialize(parsedValue);
        return deserializedValue;
    }

    // sets the persisted storage object
    _setPersistStorage = (value) => {
        const serializedValue = this._serialize(value);
        const newStorageJson = JSON.stringify(serializedValue);
        window.sessionStorage.setItem(this.persistKey, newStorageJson);        
        return this;
    }

    // sets a specific key value in the persisted storage object
    _setPersistStorageKey = (key, value) => {
        let newPersistStorage = this._getPersistStorage();
        newPersistStorage[key] = value;
        this._setPersistStorage(newPersistStorage);
        return this;
    }

    // sets the storage storage to the values saved in the persisted storage
    _rehydrateStorage = async () => {
        if (this.isStorageRehydrated) return this;

        // set each persisted key value in the storage
        const persistStorage = this._getPersistStorage();
        const persistStorageKeys = Object.keys(persistStorage)
        for(let i=0; i<persistStorageKeys.length; i++) {           
            const key = persistStorageKeys[i]; 
            await this.storage.set(key, persistStorage[key]);
        }

        this.isStorageRehydrated = true;
        return this;
    }

    // undefined values are lost during JSON.stringify
    // so _serialize converts those values to a string "__undefined__"
    _serialize = (value) => {
        const valueType = typeof value;
        // convert undefined to a string
        if (valueType === "undefined") {
            return "__undefined__";
        }

        // skip values that dont need to be processed
        if (
            value === null ||
            valueType === "string" ||
            valueType === "boolean" ||
            valueType === "number" ||
            Array.isArray(value)
        ) {
            return value;
        }

        // otherwise the value is an object, so serialize each of its keys
        let newValue = {};
        Object.keys(value).forEach(key => newValue[key] = this._serialize(value[key]));
        return newValue;
    }

    _deserialize = (serializedValue) => {
        const valueType = typeof serializedValue;
        // deserialize undefined
        if (serializedValue === "__undefined__") {
            return undefined;
        }

        // skip values that dont need to be processed
        if (
            serializedValue === null ||
            valueType === "string" ||
            valueType === "boolean" ||
            valueType === "number" ||
            Array.isArray(serializedValue)
        ) {
            return serializedValue;
        }

        // otherwise the value is an object, so deserialize each of its keys
        let newValue = {};
        Object.keys(serializedValue).forEach(key => newValue[key] = this._deserialize(serializedValue[key]));
        return newValue;
    }
}

export default SharedStoragePersistDecorator;